// Cruce de la venta contra el catálogo de HOY (`GET /wizard/catalog`), la única ficha de producto
// que el vendedor puede leer. Funciones puras: la pantalla solo compone.
//
// Lo que sale de aquí es la ficha ACTUAL del producto, no la que tenía al venderse: solo completa la
// categoría cuando la venta no la congeló, y explica por qué falta cuando no se puede leer.
import type { CatalogItem, ProductCategory } from '@/domains/sales/wizard/types';
import { PRODUCT_CATEGORY_LABEL } from '@/shared/lib/format/productCategory';

/** El catálogo todavía no llega: no se puede afirmar ni negar nada del equipo. */
export const CATALOG_LOADING_NOTE = 'Cargando la ficha del equipo desde el catálogo de tu tienda.';

/** La lectura del catálogo falló: es un «no sé», no un «no hay». */
export const CATALOG_UNREADABLE_NOTE =
    'No se pudo leer el catálogo de tu tienda, así que este dato del equipo no se puede mostrar.';

/** La venta es vieja o de otra tienda: su SKU ya no está en el catálogo que el vendedor ve. */
export const SKU_NOT_IN_CATALOG_NOTE =
    'Este equipo ya no está en el catálogo de tu tienda, así que su ficha no se puede consultar.';

/** Lo que la pantalla necesita saber del catálogo, sin acoplarse a react-query. */
export interface CatalogState {
    items: CatalogItem[] | undefined;
    isLoading: boolean;
    isError: boolean;
}

/** Si el catálogo de hoy tiene el equipo de la venta, o por qué no se sabe: el `reason` va con el guion. */
export type SaleCatalogFacts = { status: 'unresolved'; reason: string } | { status: 'found' };

/** Un catálogo que no se pudo leer y un SKU que no está en él se responden distinto. */
export function saleCatalogFacts(sku: string, catalog: CatalogState): SaleCatalogFacts {
    if (catalog.isLoading) return { status: 'unresolved', reason: CATALOG_LOADING_NOTE };
    if (catalog.isError || !catalog.items) {
        return { status: 'unresolved', reason: CATALOG_UNREADABLE_NOTE };
    }

    if (!catalog.items.some((candidate) => candidate.sku === sku)) {
        return { status: 'unresolved', reason: SKU_NOT_IN_CATALOG_NOTE };
    }
    return { status: 'found' };
}

/**
 * Categoría del equipo en español: la congelada en su línea de la venta y, si no hay, la del
 * catálogo de hoy (sin categoría es teléfono). `null` si nadie la sabe.
 */
export function saleCategoryLabel(
    sku: string,
    lines: readonly { sku: string; product: { category: ProductCategory } | null }[] | undefined,
    catalog: CatalogState,
): string | null {
    const frozen = lines?.find((line) => line.sku === sku)?.product?.category;
    if (frozen) return PRODUCT_CATEGORY_LABEL[frozen];
    const item = catalog.items?.find((candidate) => candidate.sku === sku);
    return item ? PRODUCT_CATEGORY_LABEL[item.category ?? 'PHONE'] : null;
}
